import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { Plus, Pencil, Key, X, UserPlus } from 'lucide-react'
import { mockAdminMentors, SUBJECTS, DEFAULT_COURSE_TYPES, type AdminMentor, mockAdminCourses } from '@/data/admin-mock-data'
import { getBatches } from '@/data/mentor-mock-data'
import { toast } from 'sonner'

export function AdminMentorsView() {
    const [mentors, setMentors] = useState<AdminMentor[]>(mockAdminMentors)
    const [modalOpen, setModalOpen] = useState(false)
    const [credentialsModalOpen, setCredentialsModalOpen] = useState(false)
    const [credentialsFor, setCredentialsFor] = useState<AdminMentor | null>(null)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [form, setForm] = useState({
        name: '',
        email: '',
        subject: 'Physics',
        assignedCourses: [] as string[], // Course Types
        assignedBatches: [] as string[],
        assignedSpecificCourses: [] as string[], // Specific Course Titles
        isActive: true,
        tempPassword: ''
    })

    const batches = getBatches()

    const openCreate = () => {
        setEditingId(null)
        setForm({ name: '', email: '', subject: 'Physics', assignedCourses: [], assignedBatches: [], assignedSpecificCourses: [], isActive: true, tempPassword: '' })
        setModalOpen(true)
    }

    const openEdit = (m: AdminMentor) => {
        setEditingId(m.id)
        setForm({
            name: m.name,
            email: m.email,
            subject: m.subject,
            assignedCourses: [...m.assignedCourses],
            assignedBatches: [...(m.assignedBatches || [])],
            assignedSpecificCourses: [...(m.assignedSpecificCourses || [])],
            isActive: m.isActive,
            tempPassword: ''
        })
        setModalOpen(true)
    }

    const openCreateCredentials = (m: AdminMentor) => {
        setCredentialsFor(m)
        const randomPass = Math.random().toString(36).slice(-8)
        setForm(f => ({ ...f, tempPassword: randomPass }))
        setCredentialsModalOpen(true)
    }

    const handleCreateCredentials = () => {
        if (!credentialsFor) return
        toast.success(`Credentials created for ${credentialsFor.name}. Login: ${credentialsFor.email}, Temp Password: ${form.tempPassword}. (Would send email in production)`)
        setCredentialsModalOpen(false)
        setCredentialsFor(null)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (editingId) {
            setMentors(prev => prev.map(x => x.id === editingId ? { ...x, ...form } : x))
        } else {
            setMentors(prev => [...prev, { ...form, id: `m-${Date.now()}`, createdAt: new Date().toISOString().split('T')[0] }])
        }
        setModalOpen(false)
    }

    const toggleCourseType = (course: string) => {
        setForm(f => ({
            ...f,
            assignedCourses: f.assignedCourses.includes(course) ? f.assignedCourses.filter(c => c !== course) : [...f.assignedCourses, course],
        }))
    }

    const toggleBatch = (batch: string) => {
        setForm(f => ({
            ...f,
            assignedBatches: f.assignedBatches.includes(batch) ? f.assignedBatches.filter(b => b !== batch) : [...f.assignedBatches, batch],
        }))
    }

    const toggleSpecificCourse = (courseTitle: string) => {
        setForm(f => ({
            ...f,
            assignedSpecificCourses: f.assignedSpecificCourses.includes(courseTitle) ? f.assignedSpecificCourses.filter(c => c !== courseTitle) : [...f.assignedSpecificCourses, courseTitle],
        }))
    }

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-theme-primary">Assign Mentors</h2>
                    <p className="text-sm text-theme-secondary">Create mentor credentials and assign to subjects, batches, and courses</p>
                </div>
                <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors">
                    <Plus className="h-5 w-5" />
                    Add Mentor
                </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {mentors.map((m) => (
                    <GlassmorphicCard key={m.id} className="p-5">
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-bold">
                                    {m.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                <div>
                                    <p className="font-semibold text-theme-primary">{m.name}</p>
                                    <p className="text-sm text-theme-secondary">{m.email}</p>
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => openEdit(m)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-theme-secondary" title="Edit"><Pencil className="h-4 w-4" /></button>
                                <button onClick={() => openCreateCredentials(m)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-theme-secondary" title="Create credentials"><Key className="h-4 w-4" /></button>
                            </div>
                        </div>
                        <p className="text-sm text-theme-secondary mb-1"><span className="font-medium text-theme-primary">Subject:</span> {m.subject}</p>

                        {/* Assignments Display */}
                        <div className="space-y-1 mb-3">
                            {m.assignedCourses.length > 0 && (
                                <p className="text-xs text-theme-secondary">
                                    <span className="font-medium text-theme-primary">Types:</span> {m.assignedCourses.join(', ')}
                                </p>
                            )}
                            {m.assignedBatches && m.assignedBatches.length > 0 && (
                                <p className="text-xs text-theme-secondary">
                                    <span className="font-medium text-theme-primary">Batches:</span> {m.assignedBatches.join(', ')}
                                </p>
                            )}
                            {m.assignedSpecificCourses && m.assignedSpecificCourses.length > 0 && (
                                <p className="text-xs text-theme-secondary">
                                    <span className="font-medium text-theme-primary">Courses:</span> {m.assignedSpecificCourses.join(', ')}
                                </p>
                            )}
                            {(!m.assignedCourses.length && (!m.assignedBatches || !m.assignedBatches.length) && (!m.assignedSpecificCourses || !m.assignedSpecificCourses.length)) && (
                                <p className="text-xs text-theme-muted italic">No active assignments</p>
                            )}
                        </div>

                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${m.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-200 dark:bg-slate-700 text-theme-secondary'}`}>
                            {m.isActive ? 'Active' : 'Inactive'}
                        </span>
                    </GlassmorphicCard>
                ))}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-md p-6 shadow-xl max-h-[90vh] overflow-y-auto custom-scrollbar" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary">{editingId ? 'Edit Mentor' : 'Add Mentor'}</h3>
                            <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Name</label>
                                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="Mentor name" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Email (login)</label>
                                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="mentor@ff.com" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Subject</label>
                                <select value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary">
                                    {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>

                            {/* Course Types */}
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-2">Assign to Course Types</label>
                                <div className="flex flex-wrap gap-2">
                                    {DEFAULT_COURSE_TYPES.map(c => (
                                        <label key={c} className={`flex items-center gap-1.5 cursor-pointer px-3 py-1.5 rounded-lg border transition-all ${form.assignedCourses.includes(c) ? 'bg-blue-50 dark:bg-blue-900/20 border-primary-blue text-primary-blue' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-theme-secondary'}`}>
                                            <input type="checkbox" checked={form.assignedCourses.includes(c)} onChange={() => toggleCourseType(c)} className="hidden" />
                                            <span className="text-xs font-medium">{c}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Batches */}
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-2">Assign to Batches</label>
                                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto custom-scrollbar p-1">
                                    {batches.map(b => (
                                        <label key={b} className={`flex items-center gap-1.5 cursor-pointer px-3 py-1.5 rounded-lg border transition-all ${form.assignedBatches.includes(b) ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 text-purple-600' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-theme-secondary'}`}>
                                            <input type="checkbox" checked={form.assignedBatches.includes(b)} onChange={() => toggleBatch(b)} className="hidden" />
                                            <span className="text-xs font-medium">{b}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Specific Courses */}
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-2">Assign to Specific Courses</label>
                                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto custom-scrollbar p-1">
                                    {mockAdminCourses.map(c => (
                                        <label key={c.id} className={`flex items-center gap-1.5 cursor-pointer px-3 py-1.5 rounded-lg border transition-all ${form.assignedSpecificCourses.includes(c.title) ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 text-amber-600' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-theme-secondary'}`}>
                                            <input type="checkbox" checked={form.assignedSpecificCourses.includes(c.title)} onChange={() => toggleSpecificCourse(c.title)} className="hidden" />
                                            <span className="text-xs font-medium truncate max-w-[200px]">{c.title}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded border-slate-300" />
                                <span className="text-sm text-theme-secondary">Active</span>
                            </label>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-theme-primary">Cancel</button>
                                <button type="submit" className="flex-1 py-2 rounded-lg bg-primary-blue text-white font-semibold">Save</button>
                            </div>
                        </form>
                    </GlassmorphicCard>
                </div>
            )}

            {credentialsModalOpen && credentialsFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setCredentialsModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary flex items-center gap-2"><UserPlus className="h-5 w-5" />Create Credentials</h3>
                            <button onClick={() => setCredentialsModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <p className="text-sm text-theme-secondary mb-4">Generate login credentials for <strong>{credentialsFor.name}</strong></p>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Email (Login)</label>
                                <input value={credentialsFor.email} readOnly className="w-full px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Temporary Password</label>
                                <input value={form.tempPassword} onChange={e => setForm(f => ({ ...f, tempPassword: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary font-mono" placeholder="Generate or enter" />
                                <button type="button" onClick={() => setForm(f => ({ ...f, tempPassword: Math.random().toString(36).slice(-8) }))} className="text-xs text-primary-blue hover:underline mt-1">Regenerate</button>
                            </div>
                            <p className="text-xs text-theme-muted">Credentials will be sent via email. Mentor can change password on first login.</p>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button type="button" onClick={() => setCredentialsModalOpen(false)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-theme-primary">Cancel</button>
                            <button onClick={handleCreateCredentials} className="flex-1 py-2 rounded-lg bg-primary-blue text-white font-semibold flex items-center justify-center gap-2">
                                <Key className="h-4 w-4" /> Create & Send
                            </button>
                        </div>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}
